/** Legacy: single first visit day. Migrated into {@link NPS_VISIT_DAYS_KEY}. */
export const NPS_FIRST_VISIT_DAY_KEY = "playmath_nps_first_visit_day";

/** localStorage JSON array of distinct local calendar days (YYYY-MM-DD) with a visit. */
export const NPS_VISIT_DAYS_KEY = "playmath_nps_visit_days";

/** Distinct prior calendar days required before NPS can show (not including today). */
export const NPS_REQUIRED_PRIOR_DAYS = 2;

/** Format a Date as a local calendar day key. */
export function toLocalDayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseVisitDaysJson(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (d): d is string => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)
    );
  } catch {
    return [];
  }
}

/**
 * Read persisted visit days, migrating the legacy single first-day key when present.
 * Does not write unless callers persist via {@link recordNpsVisit}.
 */
export function readNpsVisitDays(): string[] {
  try {
    if (typeof window === "undefined") return [];
    const fromNew = parseVisitDaysJson(localStorage.getItem(NPS_VISIT_DAYS_KEY));
    const legacy = localStorage.getItem(NPS_FIRST_VISIT_DAY_KEY);
    const days = new Set(fromNew);
    if (legacy && /^\d{4}-\d{2}-\d{2}$/.test(legacy)) {
      days.add(legacy);
    }
    return [...days].sort();
  } catch (error) {
    console.error("Error reading NPS visit days", { error });
    return [];
  }
}

/**
 * Record that this browser had a session today.
 * Accumulates distinct local calendar days; never removes prior days.
 * Fail-soft if storage is unavailable.
 */
export function recordNpsVisit(now: Date = new Date()): void {
  try {
    if (typeof window === "undefined") return;
    const today = toLocalDayKey(now);
    const days = readNpsVisitDays();
    if (!days.includes(today)) {
      days.push(today);
      days.sort();
    }
    localStorage.setItem(NPS_VISIT_DAYS_KEY, JSON.stringify(days));
    // Drop legacy key once migrated into the set.
    if (localStorage.getItem(NPS_FIRST_VISIT_DAY_KEY)) {
      localStorage.removeItem(NPS_FIRST_VISIT_DAY_KEY);
    }
  } catch (error) {
    console.error("Error recording NPS visit day", { error });
  }
}

/**
 * True when this browser already had sessions on at least
 * {@link NPS_REQUIRED_PRIOR_DAYS} distinct calendar days before today (local timezone).
 * Fail-closed if storage is unavailable.
 */
export function hasPriorDayNpsSession(now: Date = new Date()): boolean {
  try {
    if (typeof window === "undefined") return false;
    const today = toLocalDayKey(now);
    const priorDays = readNpsVisitDays().filter((day) => day < today);
    return priorDays.length >= NPS_REQUIRED_PRIOR_DAYS;
  } catch (error) {
    console.error("Error reading NPS prior-day session", { error });
    return false;
  }
}
